// ============================================================================
// LEARNING — the view-side formatting the two screens share.
//
// WHAT IS NOT HERE, and never will be: the pace number, the percentage, the current unit, the
// week counts. Those are DERIVED IN THE DATA CONTRACT (lib/data/demo.ts) so that an LLM asking
// the Worker "am I behind on algebra" gets the same sentence the screen prints — CLAUDE.md's
// machine-readability rule. Everything below turns a value the contract already decided into
// characters; none of it decides anything.
// ============================================================================

import { daysBetween, formatShortDate, relativeDay, weekdayName } from '@app/shared'
import type { ISODate } from '@app/shared'
import type { LearningTrackSummary } from '../../lib/data'

/** 'track-algebra' → the hue classes that track wears everywhere on both screens. */
export interface TrackHue {
  /** Text colour for the unit pill and the "Open track" link. */
  text: string
  /** The pill's ground. */
  fill: string
  /** Stroke colour for the ring and the arrow. */
  accent: string
  /** Ground for the week strip's filled bars. Written out rather than derived from `accent`:
   *  Tailwind reads literal class strings out of the source, so a name built at runtime is a
   *  class that never gets generated and a bar that silently renders transparent. */
  bar: string
  /** Border colour for the today's-item checkbox. */
  border: string
  /** The same accent, as a `var(--color-…)` reference for `--mo-rule` (motion-brief §2's
   *  hover mechanics take a CSS custom property, not a Tailwind class). */
  rule: string
}

const TEAL: TrackHue = {
  text: 'text-teal',
  fill: 'bg-teal-fill',
  accent: 'text-learning',
  bar: 'bg-learning',
  border: 'border-learning',
  rule: 'var(--color-learning)',
}

const BLUE: TrackHue = {
  text: 'text-info',
  fill: 'bg-info-fill',
  accent: 'text-school',
  bar: 'bg-school',
  border: 'border-school',
  rule: 'var(--color-school)',
}

/**
 * Learning's own hue is teal, and the first track wears it. A second track needs its own so the
 * two cards are told apart at a glance — the mockup gives Python the School blue, which is the
 * only other hue on the screen. A third track would need a third, and the design has none: rather
 * than invent one, it falls back to teal and the cards are told apart by their names.
 */
export function hueFor(index: number): TrackHue {
  return index === 1 ? BLUE : TEAL
}

/** The ring's stroke-dashoffset for a 30px radius at `percent`. */
export const RING_CIRCUMFERENCE = 2 * Math.PI * 30

export function ringOffset(percent: number): number {
  return RING_CIRCUMFERENCE * (1 - Math.min(100, Math.max(0, percent)) / 100)
}

/** '30h 10m' · '48m'. Minutes only below an hour — '0h 48m' is a number pretending to be two. */
export function durationPhrase(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return `${h}h ${String(m).padStart(2, '0')}m`
}

/**
 * What the NEXT line says about an item's date. An item the plan dated in the past is OVERDUE and
 * says so with the day it slipped from; everything else is just a day.
 */
export function scheduleNote(date: ISODate, today: ISODate): string {
  const delta = daysBetween(today, date)
  if (delta < 0) {
    return delta > -7
      ? `overdue since ${weekdayName(date, true)}`
      : `overdue since ${formatShortDate(date)}`
  }
  return relativeDay(date, today)
}

/**
 * The third pill on a track card, and the one place the two states are genuinely different:
 * a track worked today shows its run, a track that stopped shows WHEN it stopped. Neither is
 * ever inferred from the other, and a track that has never been worked says that instead of
 * showing a zero.
 */
export function activityPhrase(summary: LearningTrackSummary, today: ISODate): string {
  if (summary.streakDays > 0) {
    return `${summary.streakDays} ${summary.streakDays === 1 ? 'day' : 'days'}`
  }
  if (summary.lastActive === null) return 'not started'
  return `last: ${relativeDay(summary.lastActive, today)}`
}

/** '26 weeks' — the plan's length, from its own two dates rather than a stored number. */
export function planWeeks(start: ISODate, end: ISODate): number {
  return Math.round((daysBetween(start, end) + 1) / 7)
}

/** 'Aug 3' from the plan's import timestamp. The date is local to the stamp, never re-zoned. */
export function importedOn(importedAt: string): string {
  return formatShortDate(importedAt.slice(0, 10) as ISODate)
}
